import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LeftOutlined } from "@ant-design/icons";

import {
  Input,
  Row,
  Typography,
  Card,
  Select,
  Form,
  DatePicker,
  Col,
  Divider,
  Spin,
  InputNumber,
} from "antd";

// OTHER IMPORTS
import dayjs from "dayjs";

import { BOM } from "../../../../../services/definedMethod";
import cnt from "../../../../../services/definedConstant";

import CustomButton from "../../../../../components/custom/CustomButton";
import MainLayout from "../../../../../components/layouts/MainLayout";
import TopContent from "../../../../../components/layouts/TopContent";
import { apiConfig, labelConfig, staticConfig } from "../../../customer.config";

const { Text } = Typography;

const AddFixedDepositCharge = (props) => {
  const { userData } = props;
  const { session } = userData;

  const navigate = useNavigate();
  const { state } = useLocation();
  const [useForm] = Form.useForm();

  const [chargeId, setChargeId] = useState();
  const [feeIntervalValue, setFeeIntervalValue] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [chargesData, setChargesData] = useState([]);
  const [displayFields, setDisplayFields] = useState(false);
  const [dueDateNotRequired, setDueDateNotRequired] = useState(false);
  const [chargeTimeTypeAnnualOrMonth, setChargeTimeTypeAnnualOrMonth] =
    useState(false);

  useEffect(() => {
    defaultDate();
    getTemplate();
  }, []);

  const defaultDate = () => {
    useForm.setFieldsValue({
      feeOnMonthDay: dayjs(),
      dueDate: dayjs(),
    });
  };

  const getTemplate = () => {
    const url = `${apiConfig.savingsAccounts}/${state.id}/charges/template`;

    let body = { session };

    setIsLoading(true);

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err?.defaultUserMessage);
        setIsLoading(false);
      } else {
        let tempCharges = [];

        res?.chargeOptions?.forEach((el) => {
          tempCharges.push({
            value: el.id,
            label: `${el.name} (${el.currency?.name})`,
          });
        });

        setChargesData(tempCharges);
        setIsLoading(false);
      }
    });
  };

  const onChangeCharge = (chargeParam) => {
    setDueDateNotRequired(false);
    setChargeTimeTypeAnnualOrMonth(false);

    const params = { template: true };
    const url = `${apiConfig.charges}/${chargeParam}`;

    let body = { session, params };

    setIsLoading(true);

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err?.defaultUserMessage);
        setIsLoading(false);
      } else {
        useForm.setFieldsValue({
          amount: res.amount,
          chargeCalculationType: res?.chargeCalculationType?.value,
          chargeTimeType: res?.chargeTimeType?.value,
        });

        const type = res?.chargeTimeType?.value?.toLowerCase();

        if (type === "withdrawal fee" || type === "saving no activity fee") {
          setDueDateNotRequired(true);
        }

        if (type === "annual fee" || type === "monthly fee") {
          setChargeTimeTypeAnnualOrMonth(true);
        }

        setFeeIntervalValue(res?.feeInterval);
        setChargeId(chargeParam);
        setDisplayFields(true);
        setIsLoading(false);
      }
    });
  };

  function onCancel() {
    navigate("/detail-call-deposit-account", { state });
  }

  const onSubmit = (formValues) => {
    if (!dueDateNotRequired && !chargeTimeTypeAnnualOrMonth) {
      formValues.dateFormat = "dd MMMM yyyy";
      formValues.dueDate = formValues.dueDate
        ? BOM.DateTime(formValues.dueDate, "only-date")
        : formValues.dueDate;
    }

    if (!dueDateNotRequired && chargeTimeTypeAnnualOrMonth) {
      formValues.monthDayFormat = "dd MMMM";
      formValues.feeOnMonthDay = formValues.feeOnMonthDay
        ? BOM.DateTimeWithoutYear(formValues.feeOnMonthDay, "only-date")
        : formValues.feeOnMonthDay;

      formValues.feeInterval = feeIntervalValue;
    }

    delete formValues.chargeCalculationType;
    delete formValues.chargeTimeType;

    const url = `${apiConfig.savingsAccounts}/${state.id}/charges`;

    const body = {
      ...formValues,
      locale: "en",
      method: "POST",
      session,
    };

    setIsLoading(true);
    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err?.errors[0]?.defaultUserMessage);
        setIsLoading(false);
      } else {
        BOM.AlertMsg(cnt.SUCCESS, labelConfig.fixedDepositChargeMsg);
        navigate("/detail-call-deposit-account", { state });
        setIsLoading(false);
      }
    });
  };

  return (
    <MainLayout isEnableRightSidebar={true}>
      <TopContent
        pageTitle={labelConfig.addCallDepositCharge}
        pageItems={staticConfig.addCallDepositChargeBreadcrumbItems}
      />

      <Spin spinning={isLoading} size="large">
        <Card
          headStyle={{ borderBottomWidth: 0 }}
          style={{
            boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
            borderRadius: 20,
          }}
          title={
            <Row style={{ alignContent: "center", width: "8%" }}>
              <button
                onClick={onCancel}
                style={{
                  border: "none",
                  background: "transparent",
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <LeftOutlined />
                <Text style={{ marginLeft: 5, fontSize: 15 }}>Back</Text>
              </button>
            </Row>
          }
        >
          <Form form={useForm} layout="vertical" onFinish={onSubmit}>
            <Row>
              <Divider />

              <Col span={8} style={{ padding: "0px 8px" }}>
                <Form.Item
                  label={labelConfig.charge}
                  name="chargeId"
                  rules={[
                    {
                      required: true,
                      message: BOM.CheckReqFld(labelConfig.charge),
                    },
                  ]}
                >
                  <Select
                    filterOption={(input, option) =>
                      (option?.label?.toLowerCase() ?? "").includes(
                        input?.toLowerCase(),
                      )
                    }
                    placeholder={BOM.handlePlaceholderMsg(
                      labelConfig.charge,
                      "select",
                    )}
                    options={chargesData}
                    onChange={onChangeCharge}
                    showSearch
                  />
                </Form.Item>
              </Col>

              {displayFields && (
                <>
                  <Col span={8} style={{ padding: "0px 8px" }}>
                    <Form.Item
                      label={labelConfig.amount}
                      name="amount"
                      rules={[
                        {
                          required: true,
                          message: BOM.CheckReqFld(labelConfig.amount),
                        },
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>

                  <Col span={8} style={{ padding: "0px 8px" }}>
                    <Form.Item
                      label={labelConfig.chargeCalculation}
                      name="chargeCalculationType"
                    >
                      <Select disabled />
                    </Form.Item>
                  </Col>

                  <Col span={8} style={{ padding: "0px 8px" }}>
                    <Form.Item
                      label={labelConfig.chargeTimeType}
                      name="chargeTimeType"
                    >
                      <Select disabled />
                    </Form.Item>
                  </Col>

                  {!dueDateNotRequired && !chargeTimeTypeAnnualOrMonth && (
                    <Col span={8} style={{ padding: "0px 8px" }}>
                      <Form.Item
                        label={labelConfig.dueForCollectionOn}
                        name="dueDate"
                        rules={[
                          {
                            required: true,
                            message: BOM.CheckReqFld(labelConfig.date),
                          },
                        ]}
                      >
                        <DatePicker style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                  )}

                  {!dueDateNotRequired && chargeTimeTypeAnnualOrMonth && (
                    <Col span={8} style={{ padding: "0px 8px" }}>
                      <Form.Item
                        label={labelConfig.dueOn}
                        name="feeOnMonthDay"
                        rules={[
                          {
                            required: true,
                            message: BOM.CheckReqFld(labelConfig.date),
                          },
                        ]}
                      >
                        <DatePicker style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                  )}
                </>
              )}
            </Row>

            <Row>
              <Divider />
              <CustomButton
                content="Submit"
                htmlType="submit"
                style={{
                  width: "10%",
                  marginLeft: "auto",
                  boxShadow: "0px 0px 10px 5px rgba(0,0,0,0.1)",
                }}
                backgroundColor="#92428B"
                colorText="white"
                hoverBackground="#AD4EA7"
              />

              <CustomButton
                content="Cancel"
                style={{
                  width: "10%",
                  marginLeft: "1%",
                  boxShadow: "0px 0px 10px 5px rgba(0,0,0,0.1)",
                }}
                backgroundColor="#fff"
                colorText="black"
                hoverBackground="#F5F5F5"
                onClick={onCancel}
              />
            </Row>
          </Form>
        </Card>
      </Spin>
    </MainLayout>
  );
};

export default AddFixedDepositCharge;
